import { motion } from 'framer-motion';
import { FiImage, FiLock, FiZap, FiTrash2, FiSearch, FiMail, FiArrowRight } from 'react-icons/fi';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import CompactToolCard from '../components/CompactToolCard';
import FeatureCard from '../components/FeatureCard';
import SEO from '../components/SEO';
import Threads from '../components/Threads/Threads';
import { homeSchemas } from '../schemas';
import './Home.css';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const popularTools = [
    { title: 'JPG → PNG', fromFormat: 'JPG', toFormat: 'PNG', keywords: 'jpg png convert image' },
    { title: 'PNG → JPG', fromFormat: 'PNG', toFormat: 'JPG', keywords: 'png jpg convert image' },
    { title: 'JPG → WebP', fromFormat: 'JPG', toFormat: 'WebP', keywords: 'jpg webp convert image' },
    { title: 'PNG → WebP', fromFormat: 'PNG', toFormat: 'WebP', keywords: 'png webp convert image' },
    { title: 'WebP → JPG', fromFormat: 'WebP', toFormat: 'JPG', keywords: 'webp jpg convert image' },
    { title: 'WebP → PNG', fromFormat: 'WebP', toFormat: 'PNG', keywords: 'webp png convert image' },
    { title: 'Reduce Image Size', fromFormat: 'JPG', toFormat: 'JPG', customLink: '/image-compressor', keywords: 'compress reduce size image' },
    { title: 'Image Cropper', fromFormat: 'IMG', toFormat: 'CROP', customLink: '/image-cropper', keywords: 'crop image cut resize' },
    { title: 'Image to Base64', fromFormat: 'IMG', toFormat: 'B64', customLink: '/base64-converter?tab=toBase64', keywords: 'base64 encode image convert' },
    { title: 'Base64 to Image', fromFormat: 'B64', toFormat: 'IMG', customLink: '/base64-converter?tab=toImage', keywords: 'base64 decode image convert' },
    { title: 'AVIF → WebP', fromFormat: 'AVIF', toFormat: 'WebP', keywords: 'avif webp convert image' },
    { title: 'PNG → AVIF', fromFormat: 'PNG', toFormat: 'AVIF', keywords: 'png avif convert image' }
  ];

  const pdfTools = [
    { title: 'Merge PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-merge', iconType: 'pdf', keywords: 'merge combine pdf join' },
    { title: 'Split PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-split', iconType: 'pdf', keywords: 'split separate pdf divide' },
    { title: 'PDF to Images', fromFormat: 'PDF', toFormat: 'PNG', customLink: '/pdf-to-images', iconType: 'pdf', keywords: 'pdf image convert png jpg' },
    { title: 'Add Watermark', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-watermark', iconType: 'pdf', keywords: 'watermark pdf text logo' },
    { title: 'Organize Pages', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/pdf-organize', iconType: 'pdf', keywords: 'organize reorder pdf pages' },
    { title: 'Compress PDF', fromFormat: 'PDF', toFormat: 'PDF', customLink: '/compress-pdf', iconType: 'pdf', keywords: 'compress reduce pdf size' },
    { title: 'Text to PDF', fromFormat: 'TXT', toFormat: 'PDF', customLink: '/text-to-pdf', iconType: 'pdf', keywords: 'text pdf convert create' }
  ];

  const videoTools = [
    { title: 'Video to GIF', fromFormat: 'MP4', toFormat: 'GIF', customLink: '/video-to-gif', iconType: 'video', keywords: 'video gif convert animation' },
    { title: 'Video to Audio', fromFormat: 'MP4', toFormat: 'MP3', customLink: '/video-to-audio', iconType: 'video', keywords: 'video audio extract mp3 convert' },
    { title: 'Remove Audio', fromFormat: 'MP4', toFormat: 'MP4', customLink: '/video-audio-remover', iconType: 'video', keywords: 'remove audio video mute' },
    { title: 'Compress Video', fromFormat: 'MP4', toFormat: 'MP4', customLink: '/video-compressor', iconType: 'video', keywords: 'compress reduce video size' }
  ];

  const allTools = [...popularTools, ...pdfTools, ...videoTools];

  const filteredTools = allTools.filter(tool => {
    const searchLower = searchQuery.toLowerCase();
    return (
      tool.title.toLowerCase().includes(searchLower) ||
      tool.keywords.toLowerCase().includes(searchLower) ||
      tool.fromFormat.toLowerCase().includes(searchLower) ||
      tool.toFormat.toLowerCase().includes(searchLower)
    );
  });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.length > 0);
  };

  const handleSearchFocus = () => {
    if (searchQuery.length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay to allow clicking on results
    setTimeout(() => setShowResults(false), 200);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowResults(false);
  };

  const features = [
    {
      icon: <FiLock />,
      title: 'End-to-End Encrypted',
      description: 'Your files are processed securely and never stored on our servers after processing.'
    },
    {
      icon: <FiZap />,
      title: 'Lightning Fast',
      description: 'Powered by fast computing for instant downloads without waiting in queues.'
    },
    {
      icon: <FiTrash2 />,
      title: 'Auto-Deleted Files',
      description: 'All uploaded files are automatically deleted from our servers after 2 hours.'
    }
  ];

  return (
    <div className="home">
      <SEO 
        title="Free Online Image, PDF & Video Tools – No Signup"
        description="All-in-one toolkit to convert, compress, crop & edit images, PDFs and videos online — free, fast, and no software install needed."
        keywords="online tools, free image tools, pdf tools online, video tools free"
        path="/"
        schema={homeSchemas}
      />
      <section className="hero-section">
        <div className="hero-background">
          <Threads
            color={[1, 0.3254901960784314, 0]}
            amplitude={3}
            distance={0.2}
            enableMouseInteraction
          />
        </div>
        <div className="hero-container">
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FiZap /> 100% FREE FOREVER
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Free Online Image, PDF & Video Tools —{' '}
            <span className="gradient-text">All in One Place</span>
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Convert, compress, crop, and edit your files instantly. No signup, no software, no watermarks — just fast, free tools that work right in your browser.
          </motion.p>

          <motion.div
            className="hero-search"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="search-wrapper">
              <div className="search-container">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search 100+ free tools..."
                  className="search-input"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                />
                {searchQuery && (
                  <motion.button
                    className="clear-btn"
                    onClick={clearSearch}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ×
                  </motion.button>
                )}
                <motion.button 
                  className="search-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Search
                </motion.button>
              </div>

              {showResults && (
                <motion.div
                  className="search-results"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {filteredTools.length > 0 ? (
                    <>
                      <div className="search-results-header">
                        <span className="results-count">
                          {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'} found
                        </span>
                      </div>
                      <div className="search-results-grid">
                        {filteredTools.slice(0, 12).map((tool, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="search-result-item"
                          >
                            <CompactToolCard {...tool} />
                          </motion.div>
                        ))}
                      </div>
                      {filteredTools.length > 12 && (
                        <div className="search-results-footer">
                          +{filteredTools.length - 12} more tools
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-results">
                      <FiSearch size={32} />
                      <p>No tools found for "{searchQuery}"</p>
                      <span>Try searching for: convert, compress, merge, or split</span>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="hero-filters"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <motion.a
              href="/image-tools"
              className="filter-btn active"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Image Tools
            </motion.a>
            <motion.a
              href="/pdf-tools"
              className="filter-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              PDF Tools
            </motion.a>
            <motion.a
              href="/video-tools"
              className="filter-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Video Tools
            </motion.a>
            <motion.a
              href="https://play.google.com/store/apps/details?id=com.instasaver.codemultiverse"
              target="_blank"
              rel="noopener noreferrer"
              className="filter-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Insta Saver
            </motion.a>
          </motion.div>
        </div>
      </section>

      <section className="section popular-section">
        <div className="section-container">
          <div className="section-header">
            <motion.h2
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Image Tools
            </motion.h2>
          </div>

          <div className="compact-tools-grid">
            {popularTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <CompactToolCard {...tool} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pdf-section">
        <div className="section-container">
          <div className="section-header">
            <motion.h2
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              PDF Tools
            </motion.h2>
          </div>

          <div className="compact-tools-grid">
            {pdfTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CompactToolCard {...tool} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section video-section">
        <div className="section-container">
          <div className="section-header">
            <motion.h2
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Video Tools
            </motion.h2>
          </div>

          <div className="compact-tools-grid">
            {videoTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CompactToolCard {...tool} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section features-section">
        <div className="section-container">
          <motion.h2
            className="section-title centered"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Secure, Private, and Blazing Fast
          </motion.h2>

          <div className="features-grid">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} delay={index * 0.15} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <motion.div
            className="cta-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cta-icon">
              <FiMail />
            </div>
            <h2 className="cta-title">Have Questions or Feedback?</h2>
            <p className="cta-description">
              We'd love to hear from you! Whether you need help, have suggestions, or just want to say hi — our team is here to help.
            </p>
            <Link to="/contact">
              <motion.button
                className="cta-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
                <FiArrowRight />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
